import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import SearchBox from '../../FlowBuilder/Common/SearchBox/index.tsx';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { LeadFormActions } from '../../../Services/_actions/index.js';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { ClearIcon } from '@mui/x-date-pickers';
import { convertToReadableFormat } from '../../../utils/helper.jsx';

const HelpFile = ({ leadFormId }: { leadFormId: string }) => {
  const leadActions = LeadFormActions();
  const { showSnackBar } = useSnackBar();
  const [openGuidelineDrawer, setOpenGuidelineDrawer] = useState(false);
  const [helpFiles, setHelpFiles] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [fileDetails, setFileDetails] = useState<any>(null);
  const [showAllTabs, setShowAllTabs] = React.useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleOpenGuidelines = () => setOpenGuidelineDrawer(true);
  const handleCloseGuidelines = () => setOpenGuidelineDrawer(false);

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };
  const getHelpFiles = async (leadFormId: string) => {
    try {
      const res = await leadActions.getHelpFiles(leadFormId);
      if (res.status) {
        setHelpFiles(res.data || []);
        handleOpenGuidelines();
      } else {
        showSnackBar('No help files found', 'info');
      }
    } catch (err) {
      console.error('Error fetching help files:', err);
      showSnackBar('Error loading help files', 'error');
    }
  };

  useEffect(() => {
    const fetchFiles = async () => {
      await getHelpFiles(leadFormId);
    };
    fetchFiles();
  }, [leadFormId]);

  const fetchFileDetails = async (
    fileId: string,
    search: string = '',
    columnFilters: Record<string, string> = {}
  ) => {
    if (!fileId) return;
    try {
      const res = await leadActions.getHelperFileDetails(fileId, search, columnFilters);
      setFileDetails(res);
    } catch (err) {
      console.error('Error fetching file details:', err);
      showSnackBar('Error loading file details', 'error');
    }
  };

  useEffect(() => {
    const fileId = helpFiles[activeTab]?._id;
    if (fileId) fetchFileDetails(fileId, searchValue);
  }, [activeTab, helpFiles, searchValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const fileId = helpFiles[newValue]?._id;
    if (fileId) fetchFileDetails(fileId, searchValue);
  };
  const toDisplayString = (val: any): string => {
    if (val == null) return '';
    if (typeof val === 'string') return val;
    if (Array.isArray(val)) return val.map(toDisplayString).join(', ');
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightText = (raw: any, search: string) => {
    const text = toDisplayString(raw);
    if (!search || !search.trim()) return text;

    const esc = escapeRegExp(search.trim());
    const parts = text.split(new RegExp(`(${esc})`, 'gi')); // keep matches

    return parts.map((part, idx) =>
      idx % 2 === 1 ? (
        <span key={idx} style={{ backgroundColor: 'yellow' }}>
          {part}
        </span>
      ) : (
        <React.Fragment key={idx}>{part}</React.Fragment>
      )
    );
  };

  return (
    <Box>
      <Box sx={{ flexGrow: 1, maxWidth: 360 }}>
        <SearchBox value={searchValue} handleSearch={handleSearch} placeholder="Search files..." />
      </Box>
      {helpFiles.length > 0 ? (
        <>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 1 }}
          >
            {(showAllTabs ? helpFiles : helpFiles.slice(0, 3)).map((file) => (
              <Tab key={file._id} label={file.file_name} />
            ))}
          </Tabs>

          {!showAllTabs && helpFiles.length > 3 && (
            <Box sx={{ mb: 2 }}>
              <Button onClick={() => setShowAllTabs(true)}>View More</Button>
            </Box>
          )}
          {/* Filters from filter_columns */}
         {fileDetails?.data?.filter_columns && (
  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
    {Object.entries(fileDetails.data.filter_columns).map(([colName, filterCfg]: any) => {
      if (!filterCfg?.is_filter) return null;

      return (
        <Box key={colName} sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <SearchableDropdown
            size="small"
            options={filterCfg.sample_values.map((v: string) => ({
              label: v,
              value: v,
            }))}
            value={
              fileDetails.data.applied_filters?.column_filters?.[colName]
                ? {
                    label: fileDetails.data.applied_filters.column_filters[colName],
                    value: fileDetails.data.applied_filters.column_filters[colName],
                  }
                : null
            }
            onChange={(selected) => {
              const newFilters = {
                ...(fileDetails.data.applied_filters?.column_filters ?? {}),
                [colName]: selected?.value ?? '',
              };

              fetchFileDetails(fileDetails.data.file_id, searchValue, newFilters);
            }}
            labelKey="label"
            valueKey="value"
            label={`Filter by ${convertToReadableFormat(colName)}`}
          />
        </Box>
      );
    })}
  </Box>
)}

          {/* File Details */}
          {fileDetails && fileDetails.data?.records?.length > 0 ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {fileDetails.data.available_columns.map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 600 }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fileDetails.data.records.map((record: any, idx: number) => (
                    <TableRow key={idx}>
                      {fileDetails.data.available_columns.map((col) => (
                        <TableCell key={col}>{highlightText(record?.[col], searchValue)}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>
              {fileDetails && fileDetails.data?.records?.length === 0
                ? 'No records found for this file.'
                : helpFiles.length === 0
                  ? 'No files available'
                  : 'Loading...'}
            </Typography>
          )}
        </>
      ) : (
        <Typography>No files available</Typography>
      )}
    </Box>
  );
};
export default HelpFile;
